/**
 * Product analytics — Firebase Analytics.
 *
 * Native (iOS/Android via Capacitor) uses the native Firebase SDK through
 * `@capacitor-firebase/analytics`. On the web the same API falls back to the
 * Firebase JS SDK when a web config is provided, and to a no-op otherwise, so
 * calling `track()` is always safe.
 */
import { Capacitor } from '@capacitor/core';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { reportError } from './monitoring';

/** Canonical event names (snake_case, <=40 chars — Firebase requirement). */
export const AnalyticsEvent = {
  SIGN_UP: 'sign_up',
  LOGIN: 'login',
  LOGOUT: 'logout',
  PROFILE_CREATED: 'profile_created',
  POST_CREATED: 'post_created',
  POST_LIKED: 'post_liked',
  COMMENT_CREATED: 'comment_created',
  CONTENT_SHARED: 'share',
  MESSAGE_SENT: 'message_sent',
  OFFER_CREATED: 'offer_created',
  APPLICATION_SUBMITTED: 'application_submitted',
  CLUB_CREATED: 'club_created',
  EVENT_CREATED: 'event_created',
  LANGUAGE_CHANGED: 'language_changed',
  ACCOUNT_DELETED: 'account_deleted',
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];
export type AnalyticsParams = Record<string, string | number | boolean | undefined>;

const isNative = Capacitor.isNativePlatform();
const enabled = isNative || Boolean(import.meta.env.VITE_FIREBASE_API_KEY);

let webAnalyticsReady = false;

/** Initialise analytics once at startup. Safe to call multiple times. */
export async function initAnalytics() {
  if (!enabled) return;
  try {
    if (isNative) {
      await FirebaseAnalytics.setEnabled({ enabled: true });
      return;
    }
    if (webAnalyticsReady) return;
    // Web: the Firebase JS SDK is initialised lazily so the bundle stays small
    // and the app boots fine without a Firebase web config.
    const [{ initializeApp, getApps }, { getAnalytics, isSupported }] = await Promise.all([
      import('firebase/app'),
      import('firebase/analytics'),
    ]);
    if (!(await isSupported())) return;
    const app = getApps().length
      ? getApps()[0]
      : initializeApp({
          apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
          authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
          projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
          storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
          appId: import.meta.env.VITE_FIREBASE_APP_ID,
          measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
        });
    getAnalytics(app);
    webAnalyticsReady = true;
  } catch (error) {
    reportError(error, { source: 'initAnalytics' });
  }
}

/** Log an analytics event. Never throws. */
export async function track(name: AnalyticsEventName, params: AnalyticsParams = {}) {
  if (!enabled) {
    if (import.meta.env.DEV) console.debug('[analytics]', name, params);
    return;
  }
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined),
  ) as Record<string, string | number | boolean>;
  try {
    if (isNative) {
      await FirebaseAnalytics.logEvent({ name, params: clean });
      return;
    }
    if (!webAnalyticsReady) await initAnalytics();
    if (!webAnalyticsReady) return;
    const [{ getApps }, { getAnalytics, logEvent }] = await Promise.all([
      import('firebase/app'),
      import('firebase/analytics'),
    ]);
    if (!getApps().length) return;
    logEvent(getAnalytics(getApps()[0]), name as string, clean);
  } catch (error) {
    reportError(error, { source: 'analytics.track', event: name });
  }
}

/** Attach the signed-in user to subsequent events (no PII, id + role + plan only). */
export async function setAnalyticsUser(user: { id: string; role?: string; plan?: string } | null) {
  if (!enabled) return;
  try {
    if (isNative) {
      await FirebaseAnalytics.setUserId({ userId: user?.id ?? null });
      if (user?.role) await FirebaseAnalytics.setUserProperty({ key: 'role', value: user.role });
      if (user?.plan) await FirebaseAnalytics.setUserProperty({ key: 'plan', value: user.plan });
      return;
    }
    if (!webAnalyticsReady) await initAnalytics();
    if (!webAnalyticsReady) return;
    const [{ getApps }, { getAnalytics, setUserId, setUserProperties }] = await Promise.all([
      import('firebase/app'),
      import('firebase/analytics'),
    ]);
    if (!getApps().length) return;
    const analytics = getAnalytics(getApps()[0]);
    setUserId(analytics, user?.id ?? null);
    if (user) setUserProperties(analytics, { role: user.role ?? '', plan: user.plan ?? '' });
  } catch (error) {
    reportError(error, { source: 'analytics.setUser' });
  }
}

/** Screen view tracking (native + web). */
export async function trackScreen(screenName: string) {
  if (!enabled) return;
  try {
    if (isNative) {
      await FirebaseAnalytics.setCurrentScreen({ screenName });
      return;
    }
    await track('screen_view' as AnalyticsEventName, { screen_name: screenName });
  } catch (error) {
    reportError(error, { source: 'analytics.screen', screen: screenName });
  }
}
